import { useMemo } from 'react';

import { CircularProgress } from '@mui/material';

import DataTable from 'components/DataTableComponent/DataTable';

import { ProgressBarCheatsModal } from './components/ProgressBarsCheatsModal';
import { ProgressBarsFilters } from './components/ProgressBarsFilters';
import { ProgressBarsLayout } from './components/ProgressBarsLayout';
import { useProgressBarsActions } from './hooks/useProgressBarsActions';
import { useProgressBarsColumns } from './hooks/useProgressBarsColumns';
import { useProgressBarsData } from './hooks/useProgressBarsData';

export const ProgressBarsTable = (): React.ReactElement => {
  const {
    filteredData,
    isLoading,
    isEmpty,
    totalCount,
    totalPages,
    currentPage,
    rows,
    skuFilterValue,
    statusFilterValue,
    deleteOffer,
    toggleOfferStatus,
    setCurrentPage,
    setSkuFilterValue,
    setStatusFilterValue,
    refetch,
  } = useProgressBarsData();

  const {
    isManageEnabled,
    canUpdateOfferStatus,
    cheatsOfferId,
    setSelectedOffer,
    handleToggleActiveStatus,
    handleOpenCheats,
    handleCloseCheats,
    handleCreateNew
  } = useProgressBarsActions({ deleteOffer, toggleOfferStatus, refetch });

  const { columns } = useProgressBarsColumns({
    canUpdateOfferStatus,
    isManageEnabled,
    handleToggleActiveStatus,
    setSelectedOffer,
    onOpenCheats: handleOpenCheats
  });

  const cheatsProductsSequence = useMemo(
    () =>
      filteredData.find((offer) => offer.offerId === cheatsOfferId)
        ?.productsSequence ?? [],
    [filteredData, cheatsOfferId]
  );

  const containerClassName = 'opacity-100 pointer-events-auto';

  const emptyStateConfig = useMemo(
    () => ({
      title: 'Progress bar',
      isEmpty,
      description:
        'Build your first progress bar now and boost players engagement.',
      disableButton: !isManageEnabled,
      onClick: handleCreateNew
    }),
    [isEmpty, isManageEnabled, handleCreateNew]
  );

  if (isLoading) {
    return (
      <ProgressBarsLayout
        className={containerClassName}
        onCreateNew={handleCreateNew}
        isCreateHidden={!isManageEnabled}
      >
        <CircularProgress className="absolute top-1/2 left-1/2 h-16 w-16" />
      </ProgressBarsLayout>
    );
  }

  return (
    <ProgressBarsLayout
      className={containerClassName}
      onCreateNew={handleCreateNew}
      isCreateHidden={!isManageEnabled}
    >
      <ProgressBarsFilters
        isVisible={!isEmpty}
        skuFilterValue={skuFilterValue}
        setSkuFilterValue={setSkuFilterValue}
        statusFilterValue={statusFilterValue}
        setStatusFilterValue={setStatusFilterValue}
      />
      <div className="px-9 pb-9 pt-2.5">
        <DataTable
          data={filteredData}
          columns={columns}
          rowIdIdentifier="offerId"
          rowCount={totalCount}
          rowsPerPage={rows}
          showPagination={totalPages > 1}
          manualPagination
          pageIndex={currentPage}
          pageCount={totalPages}
          onPageChange={setCurrentPage}
          emptyStateConfig={emptyStateConfig}
          bordered
        />
      </div>
      <ProgressBarCheatsModal
        isOpen={cheatsOfferId !== null}
        onClose={handleCloseCheats}
        offerId={cheatsOfferId ?? ''}
        productsSequence={cheatsProductsSequence}
      />
    </ProgressBarsLayout>
  );
};

